// hooks/auth/useAuth.ts
import { useAuthManager } from '../../components/auth/AuthenticationManager';
import { useMemo } from 'react';
import logger from '../../utils/logging/logger';

const componentName = 'useAuth';

/**
 * Compatibility hook that provides the same API as the original useAuth hook
 * but uses the new centralized AuthenticationManager.
 * 
 * This allows components to migrate to the new system without changing their code.
 */
export const useAuth = () => {
    // Use the new AuthenticationManager context
    const authManager = useAuthManager();

    // Log hook usage for debugging
    logger.log('LogVerbose', componentName, 'useAuth hook called');

    // Create a stable API that matches the original useAuth hook
    return useMemo(() => {
        // Create a object to return that has the exact same shape
        // as the original AuthProvider context value
        return {
            // User information
            user: authManager.user,
            isAuthenticated: authManager.isAuthenticated,
            isAnonymous: authManager.isAnonymous,
            isInitializing: authManager.isInitializing,
            error: authManager.error,
            requiresRelogin: authManager.requiresRelogin,
            lostUserInfo: authManager.lostUserInfo,

            // Authentication actions
            login: authManager.login,
            logout: authManager.logout,
            convertAnonymousAccount: authManager.convertAnonymousAccount,
            checkUsernameAvailability: authManager.checkUsernameAvailability,
            refreshUserInfo: authManager.refreshUserInfo,
            clearReloginFlag: authManager.clearReloginFlag,

            // These functions are properly exposed:
            createAnonymousAccount: authManager.createAnonymousAccount,
            saveUserInfoToCookie: authManager.saveUserInfoToCookie,
            ensureUser: authManager.ensureUser,

            // REMOVED: ensureCompleteUserInfo - this should not be exposed
        };
    }, [
        // Update dependencies to match all used properties
        authManager.user,
        authManager.isAuthenticated,
        authManager.isAnonymous,
        authManager.isInitializing,
        authManager.error,
        authManager.requiresRelogin,
        authManager.lostUserInfo,
        authManager.login,
        authManager.logout,
        authManager.convertAnonymousAccount,
        authManager.checkUsernameAvailability,
        authManager.refreshUserInfo,
        authManager.clearReloginFlag,
        authManager.createAnonymousAccount,
        authManager.saveUserInfoToCookie,
        authManager.ensureUser,
        // REMOVED from dependencies: authManager.ensureCompleteUserInfo
    ]);
};

export default useAuth;